import { RunStatus } from "@kaggle/components";
import { getServices } from "../../../api/getServices";
import { ResourceUsage, Run, RunLog } from "../../types/entities/run";
import { getStore } from "../store/appStore";
import { AppAction } from "../types";
import { RunActionKey as ActionKey } from "./ActionKeys";

// Aliasing number type to improve readability below.
type RunId = number;

export interface SetRunStatusAction
  extends AppAction<
    ActionKey.SET_RUN_STATUS,
    { id: RunId; status: RunStatus }
  > {}
export interface SetRunStatusBatchedAction
  extends AppAction<
    ActionKey.SET_RUN_STATUS_BATCHED,
    (Pick<Run, "id"> & Partial<Run>)[]
  > {}
export interface FetchRunSuccessAction
  extends AppAction<ActionKey.FETCH_RUN_SUCCESS, { id: RunId }> {}
export interface UpdateResourceUsageAction
  extends AppAction<
    ActionKey.UPDATE_RESOURCE_USAGE,
    { id: RunId; resourceUsage: ResourceUsage }
  > {}
export interface UpdateRunLogsAction
  extends AppAction<ActionKey.UPDATE_RUN_LOGS, { id: RunId; logs: RunLog[] }> {}
export interface UpdateStdOutAction
  extends AppAction<
    ActionKey.UPDATE_STD_OUT,
    { id: RunId; stdOut: { key: number; value: string }[] }
  > {}
export interface UpdateStdErrAction
  extends AppAction<
    ActionKey.UPDATE_STD_ERR,
    { id: RunId; stdErr: { key: number; value: string }[] }
  > {}
export interface SetDatasetDownloadProgressMsgAction
  extends AppAction<
    ActionKey.SET_DATASET_DOWNLOAD_PROGRESS_MSG,
    { runId: RunId; messages: string[] }
  > {}

export type RunsAction =
  | SetRunStatusAction
  | SetRunStatusBatchedAction
  | FetchRunSuccessAction
  | UpdateResourceUsageAction
  | UpdateRunLogsAction
  | UpdateStdOutAction
  | UpdateStdErrAction
  | SetDatasetDownloadProgressMsgAction;

export const setRunStatus = (
  id: number,
  status: RunStatus
): SetRunStatusAction =>
  getStore().dispatch({
    type: ActionKey.SET_RUN_STATUS,
    payload: { id, status },
  });

export const fetchRunSuccess = (id: number): FetchRunSuccessAction =>
  getStore().dispatch({
    type: ActionKey.FETCH_RUN_SUCCESS,
    payload: { id },
  });

export const updateRunStatus = (
  runStatusArray: (Pick<Run, "id"> & Partial<Run>)[]
): SetRunStatusBatchedAction =>
  getStore().dispatch({
    type: ActionKey.SET_RUN_STATUS_BATCHED,
    payload: runStatusArray,
  });

export const updateResourceUsage = (
  id: RunId,
  resourceUsage: ResourceUsage
): UpdateResourceUsageAction =>
  getStore().dispatch({
    type: ActionKey.UPDATE_RESOURCE_USAGE,
    payload: { id, resourceUsage },
  });

export const updateStdStream = (
  id: RunId,
  type: "streams:stdout" | "streams:stderr",
  stdStream: { key: number; value: string }[]
) => {
  if (type === "streams:stdout") {
    return getStore().dispatch({
      type: ActionKey.UPDATE_STD_OUT,
      payload: { id, stdOut: stdStream },
    });
  } else {
    return getStore().dispatch({
      type: ActionKey.UPDATE_STD_ERR,
      payload: { id, stdErr: stdStream },
    });
  }
};

export const updateRunLogs = (id: RunId, logs: RunLog[]): UpdateRunLogsAction =>
  getStore().dispatch({
    type: ActionKey.UPDATE_RUN_LOGS,
    payload: { id, logs },
  });

export const fetchRunLogs = async (kernelRunId: number | null) => {
  const services = getServices();
  if (!kernelRunId) {
    throw new Error("Cannot fetch logs without runId (modal metadata).");
  }
  const response =
    await services.kernelsClient.kernelsService.listKernelRunLogs({
      kernelRunId,
    });
  updateRunLogs(kernelRunId, response.logs);
};

export const updateDatasetDownloadProgressMessage = (
  runId: number,
  messages: string[]
): SetDatasetDownloadProgressMsgAction =>
  getStore().dispatch({
    type: ActionKey.SET_DATASET_DOWNLOAD_PROGRESS_MSG,
    payload: { runId, messages },
  });
